import React, { memo } from 'react';
import { createPortal } from 'react-dom';
import { formatCurrency, formatDate, getStatusBadgeStyle } from '../helpers';

const ModalDetalleFacturaEmitida = ({ show, onClose, factura }) => {
    if (!show || !factura) return null;

    const retencionesList = Array.isArray(factura.retencionesList) ? factura.retencionesList : [];

    // Moneda grabada y símbolos dinámicos
    const monedaActual = factura.moneda || 'ARS';
    const simboloMoneda = monedaActual === 'USD' ? 'U$S' : '$';
    const monedaSimbolo = monedaActual === 'USD' ? 'USD' : 'ARS';

    const montoTotalFactura = parseFloat(factura.montoTotal ?? factura.precioFinal) || 0;
    const retencionesTotalesVal = parseFloat(factura.retencionesTotales) || 0;
    const cobroEsperadoVal = parseFloat(factura.cobroEsperadoBanco) || (montoTotalFactura - retencionesTotalesVal);
    const ingresoRealBancoVal = parseFloat(factura.montoIngresoBanco) || 0;
    const diferenciaCobroVal = factura.diferenciaCobro !== undefined ? parseFloat(factura.diferenciaCobro) : 0;

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-sans animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-auto max-h-[95vh] flex flex-col overflow-hidden border border-gray-100 transition-all duration-300">
                
                {/* HEADER LIGERO Y LIMPIO */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-blue-50/50 shrink-0">
                    <div className="flex items-center gap-3">
                        <span className="bg-blue-100 text-blue-600 p-2.5 rounded-xl shrink-0">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        </span>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 leading-none">
                                Detalle de Factura Emitida
                            </h3>
                            <p className="text-[10px] text-blue-800/80 font-black mt-1.5 tracking-wider uppercase select-none">
                                {factura.tipoComprobante || 'Factura'} N° {factura.numeroFactura || 'S/N'} • Venta / Comisiones ({monedaActual})
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-slate-50 rounded-full cursor-pointer"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* CUERPO DEL MODAL */}
                <div className="overflow-y-auto p-8 space-y-6 flex-grow bg-white scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                    
                    {/* CARD RESUMEN EJECUTIVA DE ESTADO & CONCILIACIÓN */}
                    <div className="bg-slate-50/80 border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                            
                            {/* ESTATUS DE COBRO */}
                            <div className="space-y-1">
                                <span className="block text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
                                    Estatus de Cobro
                                </span>
                                <span className={`inline-block px-3.5 py-1 rounded-full text-xs font-black uppercase border ${getStatusBadgeStyle(factura.estatus)}`}>
                                    ● {factura.estatus || 'Pendiente de Cobro'}
                                </span>
                            </div>

                            {/* FECHA REAL DE INGRESO AL BANCO */}
                            <div className="space-y-1 sm:border-l sm:border-slate-200/80 sm:pl-4">
                                <span className="block text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
                                    Fecha Ingreso Banco
                                </span>
                                <span className="font-mono font-bold text-slate-800 text-sm">
                                    {factura.estatus === 'Cobrada' && factura.fechaIngresoBanco 
                                        ? formatDate(factura.fechaIngresoBanco) 
                                        : 'Sin registrar'}
                                </span>
                            </div>

                            {/* CONCILIACIÓN SEMÁFORO */}
                            <div className="space-y-1 sm:border-l sm:border-slate-200/80 sm:pl-4">
                                <span className="block text-[11px] font-extrabold uppercase tracking-wider text-slate-400">
                                    Conciliación CBU
                                </span>
                                {factura.montoIngresoBanco === undefined || factura.montoIngresoBanco === '' ? (
                                    <span className="text-xs font-bold text-slate-400 italic">
                                        Sin Registro en Banco
                                    </span>
                                ) : (
                                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-xl text-xs font-mono font-black border ${
                                        diferenciaCobroVal === 0
                                            ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                            : diferenciaCobroVal < 0
                                                ? 'bg-rose-50 text-rose-800 border-rose-300'
                                                : 'bg-amber-50 text-amber-800 border-amber-300'
                                    }`}>
                                        {diferenciaCobroVal === 0 ? (
                                            '✓ Conciliado 100%'
                                        ) : (
                                            `${diferenciaCobroVal < 0 ? 'Faltante: ' : 'Excedente: '} ${formatCurrency(Math.abs(diferenciaCobroVal), monedaSimbolo)}`
                                        )}
                                    </span>
                                )}
                            </div>

                        </div>
                    </div>

                    {/* SECCIÓN I: COMPROBANTE E IDENTIFICACIÓN */}
                    <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                        <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                            Comprobante e Identificación
                        </legend>
                        
                        <div className="space-y-4 pt-2">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Fecha Emisión</span>
                                    <span className="font-mono font-bold text-slate-900 text-sm">{formatDate(factura.fechaEmision)}</span>
                                </div>
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Fecha Ingreso</span>
                                    <span className="font-mono font-bold text-slate-900 text-sm">{formatDate(factura.fechaIngreso || factura.fechaEmision)}</span>
                                </div>
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Tipo Comprobante</span>
                                    <span className="font-bold text-slate-900 text-sm">{factura.tipoComprobante || '---'}</span>
                                </div>
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">N° Factura</span>
                                    <span className="font-mono font-bold text-indigo-700 text-sm">{factura.numeroFactura || 'S/N'}</span>
                                </div>
                            </div>

                            <div className="bg-slate-50/70 p-3.5 rounded-xl border border-slate-200/80">
                                <span className="block text-[14px] font-bold text-slate-800 mb-1">Cliente (Facturado A)</span>
                                <span className="font-black text-slate-900 uppercase text-sm">{factura.clienteNombre || '---'}</span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Área / Centro de Costo</span>
                                    <span className="font-bold text-slate-800 uppercase text-xs">{factura.areaNombre || 'Sin área asignada'}</span>
                                </div>
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Concepto</span>
                                    <span className="font-bold text-slate-800 uppercase text-xs">{factura.conceptoNombre || factura.conceptoIngreso || 'Sin concepto'}</span>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    {/* SECCIÓN II: CÁLCULO DE IMPORTES E IMPUESTOS */}
                    <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                        <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                            Cálculo de Importes e Impuestos
                        </legend>

                        <div className="space-y-4 pt-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Monto Neto Gravado ({simboloMoneda})</span>
                                    <span className="font-mono text-lg font-bold text-slate-900">{formatCurrency(factura.montoNeto ?? factura.precioNeto, monedaSimbolo)}</span>
                                </div>

                                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Tasa de IVA Aplicada</span>
                                    <span className="font-mono text-lg font-bold text-slate-900">{factura.tasaIva !== undefined ? `${factura.tasaIva}%` : '21%'}</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                                <div className="bg-blue-50/70 p-4 rounded-xl border border-blue-300 flex flex-col justify-center">
                                    <span className="text-[10px] font-black text-blue-800 uppercase tracking-wider">Monto IVA</span>
                                    <span className="font-mono text-lg font-black text-blue-800 mt-0.5">{formatCurrency(factura.montoIva, monedaSimbolo)}</span>
                                </div>

                                <div className="bg-emerald-50/70 text-emerald-800 p-4 rounded-xl border border-emerald-200 flex flex-col justify-center shadow-2xs">
                                    <span className="text-[10px] font-black uppercase tracking-wider opacity-90">Monto Total (Factura)</span>
                                    <span className="font-mono text-lg font-black mt-0.5">{formatCurrency(montoTotalFactura, monedaSimbolo)}</span>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    {/* SECCIÓN III: TESORERÍA Y RETENCIONES */}
                    <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                        <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                            Tesorería y Retenciones
                        </legend>

                        <div className="space-y-6 pt-2">
                            
                            {/* BLOQUE RETENCIONES MÚLTIPLES */}
                            <div className="p-5 bg-rose-50/35 rounded-xl border border-rose-200 space-y-3">
                                <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                                    <span className="text-[14px] font-black text-rose-700 uppercase tracking-wider">Retenciones Sufridas</span>
                                    <span className="text-sm font-mono font-bold text-rose-700 bg-rose-50 px-3 py-0.5 rounded-md border border-rose-200">
                                        Total Retenido: {formatCurrency(retencionesTotalesVal, monedaSimbolo)}
                                    </span>
                                </div>

                                {retencionesList.length > 0 ? (
                                    <div className="flex flex-wrap gap-2 pt-2">
                                        {retencionesList.map((ret, idx) => (
                                            <span key={idx} className="inline-flex items-center gap-2 bg-white border border-rose-200 text-slate-800 font-bold px-3 py-1.5 rounded-xl text-xs">
                                                <span className="text-rose-700 uppercase font-black">{ret.tipo}:</span>
                                                <span className="font-mono text-sm">{formatCurrency(ret.monto, monedaSimbolo)}</span>
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs font-semibold text-slate-500 italic pt-1">
                                        Sin retenciones registradas ({formatCurrency(0, monedaSimbolo)}).
                                    </p>
                                )}
                            </div>

                            {/* INGRESOS A BANCO Y MEDIOS DE PAGO */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="bg-slate-50/70 p-3.5 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Forma de Pago</span>
                                    <span className="font-bold text-slate-800 uppercase text-xs">{factura.formaPago || 'No especificada'}</span>
                                </div>

                                <div className="bg-slate-50/70 p-3.5 rounded-xl border border-slate-200/80">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Cuenta Bancaria</span>
                                    <span className="font-bold text-slate-800 uppercase text-xs">{factura.cuentaNombre || 'Sin asignar'}</span>
                                </div>

                                <div className="bg-blue-50/40 p-3.5 rounded-xl border border-blue-200">
                                    <span className="block text-[14px] font-bold text-slate-800 mb-1">Ingreso Banco ({simboloMoneda})</span>
                                    <span className="font-mono font-black text-blue-900 text-base">
                                        {formatCurrency(ingresoRealBancoVal, monedaSimbolo)}
                                    </span>
                                </div>
                            </div>

                            {/* CARDS AUDITORÍA TIPO DASHBOARD */}
                            <div className="p-5 rounded-2xl border border-indigo-200/80 bg-gradient-to-br from-indigo-50/50 via-white to-slate-50 shadow-sm space-y-4">
                                <div className="flex items-center justify-between border-b border-indigo-100 pb-2.5">
                                    <div className="flex items-center gap-2">
                                        <span className="p-1.5 bg-indigo-100 text-indigo-700 rounded-lg">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                            </svg>
                                        </span>
                                        <span className="text-xs font-black text-slate-800 uppercase tracking-wider">Cálculo Esperado en Tesorería ({monedaActual})</span>
                                    </div>
                                    <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full uppercase tracking-widest">
                                        Auditoría de Cobro
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
                                    <div className="bg-white p-4 rounded-xl border border-slate-200/80 flex flex-col justify-between shadow-2xs">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Cobro Esperado en CBU</span>
                                        <span className="font-mono text-lg font-black text-indigo-900 mt-1">
                                            {formatCurrency(cobroEsperadoVal, monedaSimbolo)}
                                        </span>
                                        <span className="text-[10px] font-semibold text-slate-400 mt-1">
                                            Total ({formatCurrency(montoTotalFactura, monedaSimbolo)}) - Retenciones ({formatCurrency(retencionesTotalesVal, monedaSimbolo)})
                                        </span>
                                    </div>

                                    <div className="bg-white p-4 rounded-xl border border-slate-200/80 flex flex-col justify-between shadow-2xs">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Ingreso Real en Banco</span>
                                        <span className="font-mono text-lg font-black text-slate-900 mt-1">
                                            {formatCurrency(ingresoRealBancoVal, monedaSimbolo)}
                                        </span>
                                        <span className="text-[10px] font-semibold text-slate-400 mt-1">
                                            Monto asentado en extracto
                                        </span>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </fieldset>

                </div>

                {/* BOTONERA INFERIOR */}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end bg-gray-50 shrink-0">
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className="px-8 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold uppercase text-xs hover:bg-slate-50 shadow-sm transition-all transform active:scale-95 select-none cursor-pointer"
                    >
                        Cerrar
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default memo(ModalDetalleFacturaEmitida);